/**
 * Deterministic client/project color coding for the Today dashboard.
 *
 * Elements set a `--client-h` hue via style; CSS derives the final color so
 * lightness adapts per theme (see .today-client-dot in today-page.css).
 * The same seed (entity/project id or name) always yields the same hue, so a
 * client keeps its color across the agenda, timeline, and future surfaces.
 */

import type { CSSProperties } from 'react'

/** Curated hues that stay legible on both light and dark surfaces. */
const CLIENT_HUES = [226, 262, 174, 20, 340, 152, 38, 200, 288, 12] as const

function hashSeed(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  }
  return hash
}

/** Stable hue (0-360) for a client/project seed. */
export function getClientHue(seed?: string | null): number {
  if (!seed) return 226
  return CLIENT_HUES[hashSeed(seed) % CLIENT_HUES.length]
}

/** Style object that feeds the `--client-h` CSS variable. */
export function getClientHueStyle(seed?: string | null): CSSProperties {
  return { '--client-h': getClientHue(seed) } as CSSProperties
}

/** Direct color strings for places that can't go through a CSS class. */
export function getClientColor(
  seed: string | null | undefined,
  { dark = false, alpha = 1 }: { dark?: boolean; alpha?: number } = {},
): string {
  const hue = getClientHue(seed)
  const lightness = dark ? 64 : 46
  return alpha >= 1
    ? `hsl(${hue} 62% ${lightness}%)`
    : `hsl(${hue} 62% ${lightness}% / ${alpha})`
}
